/**
 * terminalPalette — the ONE sanctioned source of terminal-console hex
 * (GitHub-dark), consumed by <TerminalSurface> and any app rendering a
 * console/log view (Script Runner, System Logs).
 *
 * WHY RAW HEX IS SANCTIONED HERE
 * ──────────────────────────────
 * A terminal is the one Workbench context where Fluent `tokens.*` deliberately
 * do NOT apply: a console must read as a *fixed* dark surface in BOTH light and
 * dark Workbench themes (an operator on a NOC wall expects a console to look
 * like a console, not flip to a white page in light mode). These values are
 * theme-static by design — exactly like `palette.ts` for maps/charts.
 *
 * Kept in this component-free module (not in TerminalSurface.tsx) so the
 * surface file stays component-only for Vite fast-refresh, mirroring how
 * `palette.ts` sits beside its consumers.
 *
 * Hard rule unchanged: app code never writes raw terminal hex — import from
 * here (or read tones off <TerminalSurface.Line tone="…">).
 */

export const terminalPalette = {
  /** Surface background (GitHub dark `canvas.default`). */
  background: '#0d1117',
  /** Default foreground text. */
  foreground: '#c9d1d9',
  /** Hairline borders (header / status-bar dividers). */
  border: '#30363d',
  /** Dim text — timestamps, empty-state hints, the system/echo tone. */
  muted: '#484f58',
  /** Secondary dim text — INFO severity, neutral metadata. */
  subtle: '#8b949e',
  /** Accent blue — commands, source tags, links. */
  accent: '#58a6ff',
  /** Success green — accepted commands, the live caret. */
  success: '#3fb950',
  /** Warning amber — WARN/WARNING severity, paused state. */
  warning: '#d29922',
  /** Error red — ERROR/CRITICAL severity, failed commands. */
  danger: '#f85149',
  /** Result violet — command result payloads. */
  result: '#d2a8ff',
} as const;

/** Semantic line tones for streamed terminal output. */
export type TerminalLineTone =
  | 'info'      /* neutral progress line */
  | 'success'   /* ✓ ok */
  | 'error'     /* ✗ failed */
  | 'command'   /* the `$ …` echo */
  | 'result'    /* command result payload */
  | 'system';   /* banners / dividers / blank spacers */

const LINE_TONE_COLOR: Record<TerminalLineTone, string> = {
  info: terminalPalette.subtle,
  success: terminalPalette.success,
  error: terminalPalette.danger,
  command: terminalPalette.accent,
  result: terminalPalette.result,
  system: terminalPalette.muted,
};

/** Resolve the hex for a streamed line tone. */
export function terminalLineColor(tone: TerminalLineTone): string {
  return LINE_TONE_COLOR[tone] ?? terminalPalette.foreground;
}

/**
 * Resolve the hex for a log severity label (DEBUG…CRITICAL). Unknown
 * severities fall back to the neutral subtle tone.
 */
export function terminalSeverityColor(severity: string): string {
  switch (severity.toUpperCase()) {
    case 'DEBUG':
      return terminalPalette.muted;
    case 'INFO':
      return terminalPalette.subtle;
    case 'WARN':
    case 'WARNING':
      return terminalPalette.warning;
    case 'ERROR':
    case 'CRITICAL':
      return terminalPalette.danger;
    default:
      return terminalPalette.subtle;
  }
}
